/**
 * Playground interceptor handlers for Remote Config CRUD operations.
 * Matches API pattern: /v1/web/remote-config
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const store = new PlaygroundSessionStore('pg_remote_configs', 5);
const now = () => new Date().toISOString();

/**
 * GET /v1/web/remote-config — list all configs, optionally filtered by X-Web-Game-Id header.
 */
export function handleGetRemoteConfigs(config, match) {
    const gameId = config.headers?.['X-Web-Game-Id'] || config.headers?.['x-web-game-id'];
    let items = store.getAll();
    if (gameId) {
        items = items.filter(item => item.gameId === gameId);
    }
    return { configs: items };
}

/**
 * GET /v1/web/remote-config/:id — single config by id.
 */
export function handleGetRemoteConfigById(config, match) {
    const id = match[1];
    const item = store.getById(id);
    if (!item) {
        throw new Error(`Remote config with id ${id} not found`);
    }
    return item;
}

/**
 * POST /v1/web/remote-config — create new config.
 */
export function handleCreateRemoteConfig(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newItem = {
        ...data,
        id,
        versionInfo: {
            version: 1,
            parentEntityId: `${id}-parent`,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: data.changeDescription || 'Initial version',
            latest: true,
        },
    };
    return { remoteConfig: store.create(newItem) };
}

/**
 * PUT /v1/web/remote-config/:id — update existing config.
 */
export function handleUpdateRemoteConfig(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Remote config with id ${id} not found`);
    }
    const currentVersion = existing.versionInfo?.version || 1;
    const updated = {
        ...data,
        id,
        versionInfo: {
            ...existing.versionInfo,
            version: currentVersion + 1,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: data.changeDescription || existing.versionInfo?.versionDescription || '',
            latest: true,
        },
    };
    return { remoteConfig: store.update(id, updated) };
}

/**
 * DELETE /v1/web/remote-config/:id — remove config.
 */
export function handleDeleteRemoteConfig(config, match) {
    const id = match[1];
    store.delete(id);
    return { success: true };
}

/**
 * POST /v1/web/remote-config/:id/clone — duplicate config with new id.
 */
export function handleCloneRemoteConfig(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Remote config with id ${id} not found`);
    }
    const newId = PlaygroundSessionStore.getNextId();
    const cloned = {
        ...existing,
        id: newId,
        name: `${existing.name} (Copy)`,
        state: 'DRAFT',
        versionInfo: {
            version: 1,
            parentEntityId: `${newId}-parent`,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: `Cloned from ${existing.name}`,
            latest: true,
        },
    };
    return { remoteConfig: store.create(cloned) };
}

/**
 * POST /v1/web/remote-config/:id/rollback — rollback (mock: just return existing item).
 */
export function handleRollbackRemoteConfig(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Remote config with id ${id} not found`);
    }
    return { remoteConfig: existing };
}

/**
 * GET /v1/web/remote-config/:id/versions — return mock version history.
 */
export function handleGetRemoteConfigVersions(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Remote config with id ${id} not found`);
    }
    return [existing];
}
